<template>
  <div>
    <fancy-input v-model="start" type="datetime-local" :label="$t('revenueStatistics.startDate')" />
    <fancy-input v-model="end" type="datetime-local" :label="$t('revenueStatistics.endDate')" />
    <simple-dropdown v-model="groupFormat" :label="$t('revenueStatistics.groupFormat.label')">
      <option v-for="(data, key) in groupFormats" :key="key" :value="data">
        {{ $t(`revenueStatistics.groupFormat.${key}`) }}
      </option>
    </simple-dropdown>
    <button class="btn mt-5" @click="fetchData">
      {{ $t('overallStatistics.fetch') }}
    </button>
    <hr>
    <div v-if="statsData">
      <table>
        <thead>
          <tr>
            <th />
            <th />
            <th :colspan="allCurrencies.size * allVatRates.size">
              CRM bezahlt
            </th>
            <th :colspan="allCurrencies.size * allVatRates.size">
              CRM geladen
            </th>
          </tr>
          <tr>
            <th />
            <th />
            <template v-for="type in ['payments'/*, 'recharges' */]" :key="`header:${type}`">
              <template v-for="currency in allCurrencies" :key="`header:${type}:${currency}`">
                <th v-for="vatRate in allVatRates" :key="`header:${type}:${currency}:${vatRate}`">
                  {{ vatRate }} ({{ currency }})
                </th>
              </template>
            </template>
            <th />
          </tr>
        </thead>
        <tbody>
          <template v-for="(slotData, slot) in statsData" :key="slot">
            <tr class="border-t-2 border-black">
              <td :rowspan="slotData.rowCount + 1" class="border-t-2 border-black">
                {{ slot }}
              </td>
            </tr>
            <tr v-for="(storeData, storeId) in slotData.storeData" :key="`${slot}:${storeId}`">
              <td>
                {{ stores[storeId] ? stores[storeId].name : storeId }}
              </td>
              <template v-for="currency in allCurrencies" :key="`${slot}:${storeId}:payments:${currency}`">
                <td v-for="vatRate in allVatRates" :key="`${slot}:${storeId}:payments:${currency}:${vatRate}`">
                  {{
                    formatPrice({
                      amount: storeData.payments[currency]?.[vatRate] || 0,
                      currency,
                    })
                  }}
                </td>
              </template>
              <template v-for="currency in allCurrencies" :key="`${slot}:${storeId}:recharges:${currency}`">
                <td>
                  {{
                    formatPrice({
                      amount: Object.values(storeData.recharges[currency] ?? {})?.reduce((acc, val) => acc + parseInt(val), 0) || 0,
                      currency,
                    })
                  }}
                </td>
              </template>
            </tr>
          </template>
        </tbody>
      </table>
      <pre>
        {{ statsData }}
      </pre>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import {
  subDays, format, formatISO, parse, startOfMonth,
} from 'date-fns';
import useLoadOptions from '@/hooks/api/loadOptions';
import Multiselect from '@/components/utils/forms/Multiselect.vue';
import FancyInput from '@/components/utils/forms/FancyInput.vue';
import SimpleDropdown from '@/components/utils/forms/SimpleDropdown.vue';

import axios from '@/services/axios';
import formatPrice from '@/utils/formatPrice';
import getIdFromIri from '@/utils/getIdFromIri';
import StatisticsTable from '@/components/pages/statistics/overall_statistics/statisticsTable.vue';

export default {
  name: 'BalanceStatisticsIndexPage',
  components: {
    StatisticsTable,
    Multiselect,
    FancyInput,
    SimpleDropdown,
  },
  setup() {
    const start = ref(format(startOfMonth(new Date()), 'yyyy-MM-dd\'T\'HH:mm'));
    const end = ref(format(subDays(new Date(), 1).setHours(23, 59, 59), 'yyyy-MM-dd\'T\'HH:mm'));

    const timeSlots = ref([]);
    const statsData = ref(null);
    const stores = ref([]);
    const allCurrencies = ref(new Set());
    const allVatRates = ref(new Set());

    const groupFormats = {
      second: {
        backend: '%Y-%m-%dT%H:%i:%s+0100',
        chart: 'yyyy-MM-dd\'T\'HH:mm:ssx',
        display: 'yyyy-MM-dd\'T\'HH:mm:ss',
      },
      minute: {
        backend: '%Y-%m-%dT%H:%i+0100',
        chart: 'yyyy-MM-dd\'T\'HH:mmx',
        display: 'yyyy-MM-dd\'T\'HH:mm',
      },
      hour: {
        backend: '%Y-%m-%dT%H+0100',
        chart: 'yyyy-MM-dd\'T\'HHx',
        display: 'yyyy-MM-dd\'T\'HH',
      },
      day: {
        backend: '%Y-%m-%d+0100',
        chart: 'yyyy-MM-ddx',
        display: 'yyyy-MM-dd',
      },
      week: {
        backend: '%x-%v+0100',
        chart: 'RRRR-IIx',
        display: 'RRRR-II',
      },
      month: {
        backend: '%Y-%m+0100',
        chart: 'yyyy-MMx',
        display: 'yyyy-MM',
      },
      year: {
        backend: '%Y+0100',
        chart: 'yyyyx',
        display: 'yyyy',
      },
    };
    const groupFormat = ref(groupFormats.month);
    const {
      loadOptions: loadStoreOptions,
      loading: loadingStoreOptions,
    } = useLoadOptions('/stores', 'name');

    const {
      loadOptions: loadCustomerGroupOptions,
      loading: loadingCustomerGroupOptions,
    } = useLoadOptions('/customer_groups', 'name');

    const insertData = async (data, type) => {
      data.forEach((item) => {
        if (!Object.prototype.hasOwnProperty.call(statsData.value, item.slot)) {
          statsData.value[item.slot] = {
            rowCount: 0,
            storeData: {},
          };
        }
        if (!Object.prototype.hasOwnProperty.call(statsData.value[item.slot].storeData, item.storeId)) {
          statsData.value[item.slot].storeData[item.storeId] = {
            recharges: {},
            payments: {},
          };
          statsData.value[item.slot].rowCount += 1;
        }
        if (!Object.prototype.hasOwnProperty.call(statsData.value[item.slot].storeData[item.storeId][type], item.currency)) {
          statsData.value[item.slot].storeData[item.storeId][type][item.currency] = {};
        }
        statsData.value[item.slot].storeData[item.storeId][type][item.currency][item.vatType] = item.amountMachine;
      });
    };

    const fetchData = async () => {
      const { data: balanceRechargeData } = await axios.post('/statistics/balance-recharge', {
        format: groupFormat.value.backend,
        start: start.value,
        end: end.value,
      });
      const { data: balancePaymentData } = await axios.post('/statistics/balance-payment', {
        format: groupFormat.value.backend,
        start: start.value,
        end: end.value,
      });

      statsData.value = {};

      insertData(balanceRechargeData, 'recharges');
      insertData(balancePaymentData, 'payments');

      timeSlots.value = [];
      allCurrencies.value = new Set([
        ...balancePaymentData.map((item) => item.currency),
        ...balanceRechargeData.map((item) => item.currency),
      ]);

      allVatRates.value = new Set([
        ...balancePaymentData.map((item) => item.vatType),
        ...balanceRechargeData.map((item) => item.vatType),
      ]);

      timeSlots.value = [...new Set([
        ...balancePaymentData.map((item) => item.slot),
        ...balanceRechargeData.map((item) => item.slot),
      ])].sort();

      const storeIds = [
        ...balancePaymentData.map((x) => x.storeId),
        ...balanceRechargeData.map((x) => x.storeId),
      ];
      if (storeIds.length > 0) {
        const { data: storeData } = await axios.get('/stores', {
          params: {
            id: [...new Set(storeIds.map((i) => getIdFromIri(i)))],
            pagination: false,
          },
        });
        stores.value = {};
        storeData['hydra:member'].forEach((s) => {
          stores.value[s.id] = s;
        });
      }
    };

    return {
      loadStoreOptions,
      loadingStoreOptions,
      loadCustomerGroupOptions,
      loadingCustomerGroupOptions,
      start,
      end,
      groupFormat,
      groupFormats,
      fetchData,
      timeSlots,
      statsData,
      formatPrice,
      parse,
      format,
      allCurrencies,
      allVatRates,
      stores,
    };
  },
  computed: {
    startDate() {
      return formatISO(new Date(this.start));
    },
    endDate() {
      return formatISO(new Date(this.end));
    },
  },
};
</script>
<style lang="scss" scoped>
table {
  tr {
    &:hover {
      background-color: inherit;
    }
    &:nth-of-type(even):hover {
      background-color: var(--color-highlight);
    }
    td, th {
      padding: 1rem;
    }
  }
}
</style>
